import PropTypes from 'prop-types'
import React from 'react'
import { faComments, faCommentMedical } from '@fortawesome/free-solid-svg-icons'
import { useMutation } from '@apollo/react-hooks'

import useError from '~/hooks/useError'
import { CreateMessage } from '~/graphql/Message.gql'
import SectionTitle from '~/components/shared/SectionTitle'
import CommentForm from '../CommentForm'
import CommentList from '../CommentList'
import { isLoggedIn, getCurrentUser } from '~/utils/auth'
import withLocale from '~/hocs/locale'
import { UserPropType } from '~/utils/prop-types'

const CommentPanel = ({
  className,
  reference,
  messages,
  onMessagesLoad,
  onAuthRequire,
  commentBoxTitle,
  commentListTitle,
}) => {
  const handleOnComplete = (data) => {
    onMessagesLoad(
      data.createMessage.agendaItem?.messages.nodes ??
        data.createMessage.email?.messages.nodes ??
        data.createMessage.post?.messagesByPostId.nodes
    )
  }

  const handleOnError = error => {
    dispatchError(error)
  }

  const { dispatchError } = useError()
  const [createMesssage] = useMutation(CreateMessage, {
    onError: handleOnError,
    onCompleted: handleOnComplete,
  })
  const user = getCurrentUser()

  const onSubmit = async ({ body, asAnonymous }, { resetForm }) => {
    if (!isLoggedIn()) {
      return onAuthRequire()
    }

    await createMesssage({
      variables: {
        body,
        showAuthor: !asAnonymous,
        agendaItemId: reference.agendaItemId,
        getAgendaItemMessages: !!reference.agendaItemId,
        emailId: reference.emailId,
        getEmailMessages: !!reference.emailId,
        postId: reference.postId,
        getPostMessages: !!reference.postId,
        authorId: user.id,
      }
    })
    return resetForm({ values: '' })
  }

  return (
    <div className={className}>
      <SectionTitle icon={faCommentMedical} title={commentBoxTitle} className="mb-0" />
      <CommentForm onSubmit={onSubmit} user={user} />
      <SectionTitle icon={faComments} title={commentListTitle} className="mt-4" />
      <CommentList messages={messages} onAuthRequire={onAuthRequire} />
    </div>
  )
}

CommentPanel.propTypes = {
  reference: PropTypes.oneOfType([
    PropTypes.shape({
      agendaItemId: PropTypes.number.isRequired,
    }),
    PropTypes.shape({
      emailId: PropTypes.number.isRequired,
    }),
    PropTypes.shape({
      postId: PropTypes.number.isRequired,
    }),
  ]).isRequired,
  className: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    body: PropTypes.string,
    createdAt: PropTypes.string,
    showAuthor: PropTypes.bool,
    author: UserPropType,
    votesByReasonMessageId: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.shape({
        positive: PropTypes.bool
      }))
    })
  })).isRequired,
  commentBoxTitle: PropTypes.string.isRequired,
  commentListTitle: PropTypes.string.isRequired,
  onAuthRequire: PropTypes.func.isRequired,
  onMessagesLoad: PropTypes.func.isRequired
}

CommentPanel.defaultProps = {
  className: '',
}

export default withLocale('common', 'commentPanel')(CommentPanel)
